import React, { useState } from 'react';
import { Trophy, Users, Lock, AlertCircle, X, Target } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import './FFAMultistep.css';

const FFAMultistepCreate = ({ user, onClose, onCreated, t }) => {
  const [formData, setFormData] = useState({
    maxPlayers: 32,
    prizePool: 500,
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const getLobbiesCount = () => Math.ceil(formData.maxPlayers / 8);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.maxPlayers < 2) {
      toast.error(t('minPlayersRequired', 'Minimum 2 players required'));
      return;
    }
    if (formData.maxPlayers > 64) {
      toast.error(t('maxPlayersExceeded', 'Maximum 64 players allowed'));
      return;
    }
    setShowConfirm(true);
  };

  const confirmCreate = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/ffa-multistep/create', formData);
      toast.success(t('ffaCreated', 'FFA Tournament created!'));
      if (onCreated) {
        onCreated(response.data.tournament);
      }
      onClose();
    } catch (error) {
      console.error('Error creating FFA tournament:', error);
      toast.error(error.response?.data?.message || t('errorCreating', 'Error creating tournament'));
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="ffa-multistep-modal-overlay">
      <div className="ffa-multistep-modal ffa-multistep-create-modal">
        <div className="ffa-multistep-modal-header">
          <div className="ffa-multistep-modal-title">
            <Target className="w-6 h-6 text-orange-400" />
            <h2>{t('createFFAMultistep', 'Create FFA Tournament')}</h2>
          </div>
          <button className="ffa-multistep-modal-close" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="ffa-multistep-create-form">
          {/* Info Box */}
          <div className="ffa-info-box">
            <AlertCircle className="w-5 h-5" />
            <div>
              <strong>{t('ffaTournamentInfo', 'Free For All Tournament')}</strong>
              <p>{t('ffaInfoDesc', 'Players compete in lobbies of up to 8. Winners advance to finals. Admin approves players and manually selects winners.')}</p>
            </div>
          </div>

          {/* Max Players */}
          <div className="ffa-multistep-form-group">
            <label><Users className="w-4 h-4" /> {t('maxPlayers', 'Max Players')}</label>
            <div className="ffa-multistep-input-wrapper">
              <input
                type="number"
                min="2"
                max="64"
                value={formData.maxPlayers}
                onChange={(e) => {
                  let val = parseInt(e.target.value) || 2;
                  if (val > 64) val = 64;
                  if (val < 2) val = 2;
                  setFormData({ ...formData, maxPlayers: val });
                }}
              />
              <span className="input-suffix">
                {formData.maxPlayers} {t('players', 'Players')} ({getLobbiesCount()} {t('lobbies', 'Lobbies')})
              </span>
            </div>
            <span className="form-hint">{t('maxPlayersHint', 'Players will be split into lobbies of up to 8 players each')}</span>
          </div>

          {/* Prize Pool */}
          <div className="ffa-multistep-form-group">
            <label><Trophy className="w-4 h-4" /> {t('prizePool', 'Prize Pool')}</label>
            <div className="ffa-multistep-input-wrapper">
              <input
                type="number"
                min="0"
                max="1000000"
                value={formData.prizePool}
                onChange={(e) => setFormData({ ...formData, prizePool: parseInt(e.target.value) || 0 })}
              />
              <span className="input-suffix">{t('points', 'Points')}</span>
            </div>
            <span className="form-hint">{t('prizePoolHint', 'Only the grand winner receives the prize')}</span>
          </div>

          {/* Password */}
          <div className="ffa-multistep-form-group">
            <label><Lock className="w-4 h-4" /> {t('password', 'Password')} ({t('optional', 'Optional')})</label>
            <input
              type="text"
              placeholder={t('leaveEmptyForPublic', 'Leave empty for public')}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
          </div>

          {/* Tournament Structure */}
          <div className="ffa-structure-preview">
            <h4>{t('tournamentStructure', 'Tournament Structure')}</h4>
            <div className="structure-rounds">
              <div className="structure-round">
                <span className="round-label">{t('round1', 'Round 1')}</span>
                <span className="round-desc">{getLobbiesCount()} {t('lobbiesOf8', 'lobbies (max 8 players each)')}</span>
              </div>
              <div className="structure-arrow">→</div>
              <div className="structure-round finals">
                <span className="round-label">{t('finals', 'Finals')}</span>
                <span className="round-desc">{getLobbiesCount()} {t('winners', 'winners compete')}</span>
              </div>
              <div className="structure-arrow">→</div>
              <div className="structure-round winner">
                <Trophy className="w-4 h-4" />
                <span className="round-label">{t('grandWinner', 'Grand Winner')}</span>
              </div>
            </div>
          </div>

          {/* Cost Summary */}
          <div className="ffa-multistep-cost-summary">
            <h4>{t('tournamentSummary', 'Tournament Summary')}</h4>
            <div className="cost-row">
              <span>{t('maxPlayers', 'Max Players')}</span>
              <span>{formData.maxPlayers}</span>
            </div>
            <div className="cost-row">
              <span>{t('lobbies', 'Lobbies')}</span>
              <span>{getLobbiesCount()}</span>
            </div>
            <div className="cost-row">
              <span>{t('rounds', 'Rounds')}</span>
              <span>2 ({t('round1AndFinals', 'Round 1 + Finals')})</span>
            </div>
            <div className="cost-row prize">
              <span>{t('prizePool', 'Prize Pool')}</span>
              <span>{formData.prizePool} {t('pts', 'pts')}</span>
            </div>
            <div className="cost-row free-note">
              <span>* {t('freeForAdmins', 'Free to create for admins')}</span>
            </div>
          </div>

          <button type="submit" className="ffa-multistep-submit-btn" disabled={loading}>
            {loading ? t('creating', 'Creating...') : t('createTournament', 'Create Tournament')}
          </button>
        </form>

        {showConfirm && (
          <div className="ffa-multistep-confirm-overlay">
            <div className="ffa-multistep-confirm-modal">
              <h3>{t('confirmCreate', 'Confirm Tournament Creation')}</h3>
              <div className="confirm-details">
                <p><strong>{t('type', 'Type')}:</strong> {t('freeForAll', 'Free For All')}</p>
                <p><strong>{t('maxPlayers', 'Max Players')}:</strong> {formData.maxPlayers}</p>
                <p><strong>{t('lobbies', 'Lobbies')}:</strong> {getLobbiesCount()}</p>
                <p><strong>{t('prizePool', 'Prize Pool')}:</strong> {formData.prizePool} {t('pts', 'pts')}</p>
                {formData.prizePool > 0 && (
                  <p className="warning-text">
                    <AlertCircle className="w-4 h-4" />
                    {formData.prizePool} {t('pointsDeducted', 'points will be deducted from your balance')}
                  </p>
                )}
              </div>
              <div className="confirm-actions">
                <button className="confirm-btn confirm-yes" onClick={confirmCreate} disabled={loading}>
                  {loading ? t('creating', 'Creating...') : t('confirm', 'Confirm')}
                </button>
                <button className="confirm-btn confirm-no" onClick={() => setShowConfirm(false)} disabled={loading}>
                  {t('cancel', 'Cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FFAMultistepCreate;
